import { RpcSocket, setRequestMode } from "@/database";
import { useAppSelector, RootState, store } from "../store";
import { useEffect, useRef } from "react";
import { checkAndReloadIfNewVersion } from "@/utils/versionCheck";

import {
  setMediaDetails,
  setSubtitleList,
  updateMediaInMediaList,
  updateSocialInMediaList,
  upsertSocialInMediaDetails,
  addPendingSocialUpdate,
} from "@/store/media/media.slice";
import {
  updateBusinessInBusinessList,
  updateProductInProductList,
  setCurrentProduct,
  setCurrentBusiness,
} from "@/store/constants/constants.slice";
import { setRemoveFile, updateFileProgress } from "@/store/upload/upload.slice";
import { setNotifications } from "@/store/app/app.slice";
import {
  notifyNetworkError,
  notifyNetworkRecovered,
} from "@/utils/networkStatus";
const progressMapping: Record<number, number> = {
  3: 10,
  4: 50,
  5: 80,
  6: 100,
};
const fakeProgressState: Record<string, any> = {};
const completedProgressState: Record<string, boolean> = {}; // Track completed IDs

const isMediaInCurrentList = (id: string | number | undefined | null) => {
  if (id == null) return false;
  const list = store.getState().media.mediaList?.list || [];
  return list.some((m: any) => String(m?.id) === String(id));
};

const startFakeProgress = (id: string) => {
  if (fakeProgressState[id] || completedProgressState[id]) return; // Prevent multiple intervals or restart if completed
  let progress = 0;

  const interval = setInterval(() => {
    if (progress >= 90) {
      clearInterval(interval); // Stop fake progress at 90% max
    } else {
      progress += 10; // Increment progress by 10 each second
      store.dispatch(
        updateFileProgress({
          identifier: id,
          progress: progress,
        })
      );
    }
  }, 1000);

  fakeProgressState[id] = interval; // Store interval to clear later
};

const stopFakeProgress = (id: string) => {
  const interval = fakeProgressState[id];
  if (interval) {
    clearInterval(interval); // Stop the interval
    delete fakeProgressState[id];
  }
};

export function useSocket() {
  const { isLoggedIn } = useAppSelector((state: RootState) => state.auth.user);
  const socketRef = useRef(new ControllerSocket());

  useEffect(() => {
    // if (!token || !isLoggedIn) {
    //   console.log('❌ No token or not logged in, disconnecting socket');
    //   socketRef.current.disconnectSocket();
    //   return;
    // }
    // console.log('🔌 Token available, connecting socket...');
    socketRef.current.connectSocket();
    return () => {
      // console.log('🧹 Cleaning up socket connection');
      socketRef.current.disconnectSocket();
    };
  }, [isLoggedIn]);

  return {
    socketWrapper: socketRef.current,
  };
}

const eventHandlers: Record<string, (data: any) => void> = {
  "notification.create": (data) => {
    const { dispatch, getState } = store;
    const currentNotifications = getState().app.notifications.list || [];
    const newNotification = data.result;
    const existingNotificationIndex = currentNotifications.findIndex(
      (notification: any) => notification.id === newNotification.id
    );
    let updatedNotifications;
    if (existingNotificationIndex !== -1) {
      updatedNotifications = [
        newNotification,
        ...currentNotifications.filter(
          (_: any, i: number) => i !== existingNotificationIndex
        ),
      ];
    } else {
      updatedNotifications = [newNotification, ...currentNotifications];
    }
    dispatch(setNotifications({ list: updatedNotifications }));
  },

  "media.update": (data) => {
    // console.log(':media.update events:All data:', data, ' and status : ', data.result.status);
    const { id, status, _ } = data.result;
    updateFakeProgress(id, status);
    // const currentPath = window.location.pathname;
    // const isOnMediaVideoPage = currentPath.includes('/media/video');
    if (
      (store.getState().media.isFilter === false || isMediaInCurrentList(id)) &&
      status !== -1 &&
      status !== -2
    ) {
      // console.log('media.update events:data.result', data.result);
      store.dispatch(updateMediaInMediaList({ media: data.result }));
    }

    //Now update the media details for edit
    const currentMediaDetailsFoEdit = store.getState().media.mediaDetails.media;
    const updatedMedia = { ...currentMediaDetailsFoEdit, ...data.result };
    // console.log(':media.update events:All data: ', updatedMedia, ' and currentMedia: ', id);
    // const isFromChopper = currentMediaDetailsFoEdit?.source === "chopper";
    // if (!isFromChopper && store.getState().media.isFilter === true) return;
    if (currentMediaDetailsFoEdit.id === id) {
      // console.log(':media.update events:All data: id same', updatedMedia);
      store.dispatch(setMediaDetails({ data: { media: updatedMedia } }));
    }
  },

  "business.update": (data) => {
    const { dispatch, getState } = store;
    const currentBusiness = getState().constants.currentBusiness;
    const updatedBusiness = { ...currentBusiness, ...data.result };

    dispatch(updateBusinessInBusinessList({ data: data.result }));
    dispatch(setCurrentBusiness(updatedBusiness));
  },
  "media.download": (data) => {
    const download = process.env.NX_APP_DOWNLOAD_URL ?? "";
    const downloadUrl = `${download}/download?id=${data.result.damid}`;
    window.open(downloadUrl, "_blank");
  },
  "media.subtitle": (data) => {
    const currentList = store.getState().media.subtitleList.list || [];
    const currentMediaDetails = store.getState().media.mediaDetails.media;

    // Check if the subtitle's media ID matches the current media details ID
    if (data.result.id === currentMediaDetails?.id) {
      if (currentList.length === 0) {
        store.dispatch(setSubtitleList({ data: { list: [data.result] } }));
      } else {
        // Check if the subtitle ID already exists in the list
        const subtitleExists = currentList.some(
          (subtitle) => subtitle.subtitle_id === data.result.subtitle_id
        );
        if (!subtitleExists) {
          // Add new subtitle to the list
          store.dispatch(
            setSubtitleList({ data: { list: [...currentList, data.result] } })
          );
        } else {
          // Update existing subtitle
          const updatedList = currentList.map((subtitle) =>
            subtitle.subtitle_id === data.result.subtitle_id
              ? { ...subtitle, status: data.result.status }
              : subtitle
          );
          store.dispatch(setSubtitleList({ data: { list: updatedList } }));
        }
      }
    }
  },
  "media.social": (data) => {
    const { id, media: mediaId } = data.result;
    if (
      store.getState().media.isFilter === false ||
      isMediaInCurrentList(mediaId)
    ) {
      store.dispatch(updateSocialInMediaList({ media: data.result }));
    }

    // Enrich the social payload with image from channel list if missing
    const updatedSocial: any = { ...data.result };
    if (!updatedSocial.id) {
      updatedSocial.id = id;
    }
    if (!updatedSocial.image) {
      const channelId = data.result.channel_id;
      if (channelId) {
        const socialChannelsList =
          store.getState().app.userSocialChannels.socialChannelsList;
        const matchedChannel = socialChannelsList.find(
          (ch: any) => String(ch.id) === String(channelId)
        );
        if (matchedChannel?.image) {
          updatedSocial.image = matchedChannel.image;
        }
      }
    }

    // Always queue so we never lose updates when media details load after socket events
    const mediaIdStr = mediaId != null ? String(mediaId) : "";
    if (mediaIdStr) {
      store.dispatch(
        addPendingSocialUpdate({ mediaId: mediaIdStr, social: updatedSocial })
      );
    }

    // Apply immediately if we're currently viewing this media
    const currentMediaDetailsFoEdit = store.getState().media.mediaDetails.media;
    if (
      currentMediaDetailsFoEdit?.id != null &&
      String(currentMediaDetailsFoEdit.id) === mediaIdStr
    ) {
      store.dispatch(upsertSocialInMediaDetails({ social: updatedSocial }));
    }
  },
  "product.update": (data) => {
    const { dispatch, getState } = store;
    const currentProduct = getState().constants.currentProduct;
    const updatedProduct = { ...currentProduct, ...data.result };

    dispatch(updateProductInProductList({ data: data.result }));
    dispatch(setCurrentProduct(updatedProduct));
  },
  "socket.welcome": (data) => {
    //Handle application refresh here, if a new version has been deployed:
    if (data.result && data.result.version) {
      checkAndReloadIfNewVersion(data.result.version);
    }
  }, // No-op handler
  "socket.error": (data) => {
    console.error("socket.error", data);
    window.location.href = "/";
  },
};

export class ControllerSocket {
  socket!: RpcSocket;
  private deliberateClose = false;

  // Handle socket state changes
  private handleSocketState = (state: number) => {
    console.log(
      "🔄 Socket state changed:",
      state,
      "at:",
      new Date().toISOString()
    );

    switch (state) {
      case 1: // SOCKET_STATE.OPEN
        notifyNetworkRecovered();
        this.handleSocketOpen();
        break;
      case 3: // SOCKET_STATE.CLOSED
        if (!this.deliberateClose) notifyNetworkError();
        this.handleSocketClosed();
        break;
      case 0: // SOCKET_STATE.CONNECTING
        console.log("⏳ Socket is connecting...");
        break;
    }
  };

  // Handle socket open state
  private handleSocketOpen = () => {
    console.log("✅ Socket is open, waiting for socket to be fully ready...");

    this.waitForSocketReady(20000).then((isReady) => {
      if (isReady) {
        this.enableSocketMode();
      } else {
        console.error(
          "⏰ Socket not ready after 20 seconds, keeping HTTP mode"
        );
      }
    });
  };

  // Handle socket closed state
  private handleSocketClosed = () => {
    console.log("❌ Socket is closed, disabling socket mode");
    setRequestMode({ useSocket: false, socketInstance: undefined });
  };

  // Handle socket messages
  private handleSocketMessage = (data: any) => {
    console.log("📨 Socket message received:", data);
    const handler = eventHandlers[data.method];
    if (handler) {
      handler(data);
    } else {
      console.warn(`⚠️ Unhandled event: ${data.method}`);
    }
  };

  // Enable socket mode with logging
  private enableSocketMode = (): boolean => {
    if (this.isSocketReady()) {
      console.log("🎯 Socket is ready, enabling socket mode");
      setRequestMode({ useSocket: true, socketInstance: this.socket });
      console.log(
        "🚀 Socket mode enabled successfully at:",
        new Date().toISOString()
      );
      console.log("🎉 All future requests will now use socket automatically!");
      return true;
    } else {
      console.log("❌ Cannot enable socket mode - socket not ready");
      return false;
    }
  };

  // Create socket URL
  private createSocketUrl = (token?: string): string => {
    const baseUrl = process.env.NX_APP_WEBSOCKET_URL;
    console.log("🔌 Creating socket URL:", baseUrl, " with token:", token);
    return `${baseUrl}/socket`;
  };

  // Connect to socket
  connectSocket = () => {
    console.log("🔌 Connecting to socket function called:");
    this.deliberateClose = false;
    const socketUrl = this.createSocketUrl();
    console.log(
      "🔌 Connecting to socket:",
      socketUrl,
      " started at :",
      new Date().toISOString()
    );

    this.socket = new RpcSocket({
      id: "controller-b2b",
      url: socketUrl,
      stateCB: this.handleSocketState,
      messageCB: this.handleSocketMessage,
    });
  };

  disconnectSocket = () => {
    if (this.socket) {
      this.deliberateClose = true;
      this.socket.closeSocket();
      // Disable socket mode when socket is closed
      setRequestMode({ useSocket: false, socketInstance: undefined });
    }
  };

  isSocketReady = () => {
    return this.socket && typeof this.socket.sendRequest === "function";
  };

  getSocketStatus = () => {
    return {
      hasSocket: !!this.socket,
      hasSendRequest: typeof this.socket?.sendRequest === "function",
      socketInstance: this.socket,
    };
  };

  // Helper function to check socket readiness
  private checkSocketReadiness = (elapsed: number): boolean => {
    if (this.isSocketReady()) {
      console.log("✅ Socket is ready after", elapsed, "ms");
      return true;
    }
    return false;
  };

  // Helper function to handle socket timeout
  private handleSocketTimeout = (
    elapsed: number,
    timeoutMs: number
  ): boolean => {
    if (elapsed >= timeoutMs) {
      console.error("⏰ Socket not ready after", timeoutMs, "ms timeout");
      return true;
    }
    return false;
  };

  // Main socket readiness check function
  waitForSocketReady = (timeoutMs: number = 20000): Promise<boolean> => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      console.log(
        "⏱️ Starting socket readiness check with",
        timeoutMs,
        "ms timeout"
      );

      const checkSocket = () => {
        const status = this.getSocketStatus();
        const elapsed = Date.now() - startTime;

        // Log current status
        console.log(
          `🔍 Socket check #${
            Math.floor(elapsed / 100) + 1
          } (${elapsed}ms elapsed):`,
          status
        );

        // Check if socket is ready
        if (this.checkSocketReadiness(elapsed)) {
          // Immediately enable socket mode when socket becomes ready
          setRequestMode({ useSocket: true, socketInstance: this.socket });
          console.log(
            "🚀 Socket mode enabled immediately when socket became ready"
          );
          resolve(true);
          return;
        }

        // Check for timeout
        if (this.handleSocketTimeout(elapsed, timeoutMs)) {
          resolve(false);
          return;
        }

        // Check again in 100ms
        setTimeout(checkSocket, 100);
      };

      // Start checking immediately
      checkSocket();
    });
  };
}

function updateFakeProgress(id: string, status: number) {
  if (id && status !== undefined) {
    if (status == 5) {
      stopFakeProgress(id);
      store.dispatch(
        updateFileProgress({
          identifier: id,
          progress: 100,
        })
      );
      completedProgressState[id] = true;

      store.dispatch(setRemoveFile({ identifier: id }));
    }
    if (status == -6) {
      store.dispatch(setRemoveFile({ identifier: id }));
    }

    if (status !== 6) {
      if (!fakeProgressState[id]) {
        startFakeProgress(id);
      }
    } else if (status === 6) {
      stopFakeProgress(id);
      store.dispatch(
        updateFileProgress({
          identifier: id,
          progress: 100,
        })
      );
      completedProgressState[id] = true; // Mark as completed
    } else if (progressMapping.hasOwnProperty(status)) {
      stopFakeProgress(id);
      store.dispatch(
        updateFileProgress({
          identifier: id,
          progress: progressMapping[status],
        })
      );
    }
  }
}
